import React from 'react';
import { motion } from 'framer-motion';
import { ShieldCheck, Users, Award, ArrowRight } from 'lucide-react';
export function Hero() {
  return <section className="relative pt-40 pb-20 lg:pt-48 lg:pb-28 overflow-hidden bg-surface" data-id="element-184">
      {/* Background decorative elements */}
      <div className="absolute top-0 right-0 -translate-y-12 translate-x-1/3 w-[800px] h-[800px] bg-navy/5 rounded-full blur-3xl pointer-events-none" data-id="element-185" />
      <div className="absolute bottom-0 left-0 translate-y-1/3 -translate-x-1/3 w-[600px] h-[600px] bg-accent/10 rounded-full blur-3xl pointer-events-none" data-id="element-186" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative" data-id="element-187">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-8 items-center" data-id="element-188">
          {/* Left Column: Copy */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.6
        }} className="max-w-2xl" data-id="element-189">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-navy/5 text-navy font-medium text-sm mb-6 border border-navy/10" data-id="element-190">
              <span className="flex h-2 w-2 rounded-full bg-accent" data-id="element-191"></span>
              Independent Agency • Multiple Top Carriers
            </div>

            <h1 className="text-5xl lg:text-6xl font-bold leading-[1.1] mb-6 text-balance" data-id="element-192">
              Putting the care back in{' '}
              <span className="text-accent" data-id="element-193">healthcare.</span>
            </h1>

            <p className="text-lg text-slate-600 mb-8 text-balance leading-relaxed" data-id="element-194">
              KRT Insurance Services & Consulting is your trusted independent
              partner for healthcare, employee benefits, and workers' comp
              solutions across California. We deliver personalized service
              backed by 20+ top carriers — no bias, no hidden fees, just honest
              advice.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 mb-12" data-id="element-195">
              <button className="bg-navy hover:bg-navy-light text-white font-semibold py-3.5 px-8 rounded-full transition-all transform hover:-translate-y-0.5 shadow-lg flex items-center justify-center gap-2" data-id="element-196">
                Get Your Free Quote
                <ArrowRight className="w-4 h-4" data-id="element-197" />
              </button>
              <a href="tel:7147264999" className="bg-white hover:bg-slate-50 text-navy border border-slate-200 font-semibold py-3.5 px-8 rounded-full transition-colors flex items-center justify-center gap-2 shadow-sm" data-id="element-198">
                Call (714) 726-4999
              </a>
            </div>

            {/* Trust Strip */}
            <div className="grid grid-cols-3 gap-4 pt-8 border-t border-slate-200/60" data-id="element-199">
              <div data-id="element-200">
                <div className="flex items-center gap-2 text-navy font-bold text-xl mb-1" data-id="element-201">
                  <Award className="w-5 h-5 text-accent" data-id="element-202" />
                  25+
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-203">
                  Years Experience
                </div>
              </div>
              <div data-id="element-204">
                <div className="flex items-center gap-2 text-navy font-bold text-xl mb-1" data-id="element-205">
                  <Users className="w-5 h-5 text-accent" data-id="element-206" />
                  10k+
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-207">
                  Families Served
                </div>
              </div>
              <div data-id="element-208">
                <div className="flex items-center gap-2 text-navy font-bold text-xl mb-1" data-id="element-209">
                  <ShieldCheck className="w-5 h-5 text-accent" data-id="element-210" />
                  A+
                </div>
                <div className="text-sm text-slate-500 font-medium" data-id="element-211">
                  BBB Rating
                </div>
              </div>
            </div>
          </motion.div>

          {/* Right Column: Interactive Quote Card & Visual */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.95
        }} animate={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 0.6,
          delay: 0.2
        }} className="relative lg:ml-auto w-full max-w-md" data-id="element-212">
            {/* Decorative image behind the card */}
            <div className="absolute -top-6 -right-6 w-full h-full bg-navy rounded-3xl overflow-hidden shadow-2xl" data-id="element-213">
              <img src="https://images.unsplash.com/photo-1573497019940-1c28c88b4f3e?ixlib=rb-4.0.3&auto=format&fit=crop&w=1000&q=80" alt="Friendly insurance agent" className="w-full h-full object-cover opacity-60 mix-blend-overlay" data-id="element-214" />
            </div>

            {/* Quote Form Card */}
            <div className="relative bg-white rounded-2xl shadow-float p-8 border border-slate-100" data-id="element-215">
              <h3 className="font-serif text-2xl font-bold text-navy mb-2" data-id="element-216">
                Find your plan
              </h3>
              <p className="text-slate-500 text-sm mb-6" data-id="element-217">
                Compare rates from top carriers in minutes.
              </p>

              <form className="space-y-4" onSubmit={e => e.preventDefault()} data-id="element-218">
                <div data-id="element-219">
                  <label htmlFor="coverage" className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-220">
                    What do you need coverage for?
                  </label>
                  <select id="coverage" className="w-full bg-slate-50 border border-slate-200 text-slate-900 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent appearance-none font-medium" defaultValue="" data-id="element-221">
                    <option value="" disabled data-id="element-222">
                      Select coverage type...
                    </option>
                    <option value="medicare" data-id="element-223">Medicare Insurance</option>
                    <option value="health" data-id="element-224">Individual & Family Health</option>
                    <option value="life" data-id="element-225">Life Insurance</option>
                    <option value="dental" data-id="element-226">Dental & Vision</option>
                    <option value="disability" data-id="element-227">Disability Insurance</option>
                    <option value="business" data-id="element-228">Employer Group Benefits</option>
                  </select>
                </div>

                <div data-id="element-229">
                  <label htmlFor="zip" className="block text-sm font-semibold text-slate-700 mb-1.5" data-id="element-230">
                    Zip Code
                  </label>
                  <input type="text" id="zip" placeholder="e.g. 90210" className="w-full bg-slate-50 border border-slate-200 text-slate-900 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-accent focus:border-transparent font-medium" data-id="element-231" />
                </div>

                <button className="w-full bg-accent hover:bg-accent-hover text-navy-dark font-bold text-lg py-3.5 rounded-xl transition-colors mt-2 shadow-sm" data-id="element-232">
                  Start My Quote
                </button>

                <p className="text-xs text-center text-slate-400 mt-4" data-id="element-233">
                  Secure, free, and no obligation to enroll.
                </p>
              </form>
            </div>
          </motion.div>
        </div>
      </div>
    </section>;
}